import { CartCallToAction } from "@omni/types/api/cart/bff/shared/cartCallToAction.model";
import { CartItem } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { CallToActionType } from "@omni/types/api/cart/bff/shared/enums/callToActionType.enum";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { head } from "@omni/utils/array.utils";
import { ReactElement } from "react";

import {
    CTAAction,
    CTAActions
} from "@/redux/modules/api/data/cart/@types/cart.types";
import acceptNegativeDeliveryPromiseAction from "@/utils/callToActions/shoppingCart/actions/acceptNegativeDeliveryPromise";
import acceptPositiveDeliveryPromiseAction from "@/utils/callToActions/shoppingCart/actions/acceptPositiveDeliveryPromise";
import changeDelivery from "@/utils/callToActions/shoppingCart/actions/changeDelivery";
import changeECornerStoreAction from "@/utils/callToActions/shoppingCart/actions/changeECornerStore";
import changeQuantitiesAction from "@/utils/callToActions/shoppingCart/actions/changeQuantities";
import changeStoreAction from "@/utils/callToActions/shoppingCart/actions/changeStore";
import changeStoreAndCnCAction from "@/utils/callToActions/shoppingCart/actions/changeStoreAndCnC";
import removeArticleAction from "@/utils/callToActions/shoppingCart/actions/removeArticle";
import baseCTAVisual from "@/utils/callToActions/shoppingCart/visuals/baseCTAVisual";
import changeQuantityCTAVisual from "@/utils/callToActions/shoppingCart/visuals/changeQuantityCTAVisual";
import changeStoreVisual from "@/utils/callToActions/shoppingCart/visuals/changeStore";
import { underScoreToCamelCase } from "@/utils/textUtils";

export type ExplodedCTA = {
    type: CallToActionType;
    parameters: Array<string>;
    sku?: string;
    productParameters: Array<string>;
};

export const explodeCTAProductList = (
    cta: CartCallToAction
): Array<ExplodedCTA> => {
    return cta.products.map<ExplodedCTA>((p) => ({
        type: cta.type,
        parameters: cta.parameters || [],
        sku: p.sku,
        productParameters: p.parameters || []
    }));
};

export const getAction = (
    ctaType: CallToActionType,
    hasProduct: boolean
): {
    closable?: boolean;
    // extended means new things could be rendered as the outcome of the action (CHANGE_STORE => storepicker)
    // meaning that the original cta dialog will be closed, extended components still have access to the CallToACtionsContext
    isExtended?: boolean;
    isSilentAction?: boolean;
    action: CTAAction | null;
    visual?: (product: CartItem | null, action: CTAActions) => ReactElement;
} => {
    const managedCtas = {
        [CallToActionType.REMOVE_ARTICLE]: {
            visual: baseCTAVisual,
            action: removeArticleAction
        },
        [CallToActionType.REMOVE_ACCESSORY_ARTICLE]: {
            isSilentAction: false,
            visual: undefined,
            action: removeArticleAction
        },
        [CallToActionType.CHANGE_TO_HOME_DELIVERY]: {
            visual: baseCTAVisual,
            action: changeDelivery(DeliveryType.HomeDelivery)
        },
        [CallToActionType.CHANGE_TO_CNC]: {
            visual: baseCTAVisual,
            action: changeDelivery(DeliveryType.ClickAndCollect)
        },
        [CallToActionType.ACCEPT_NEGATIVE_DELIVERY_PROMISE]: {
            visual: baseCTAVisual,
            action: acceptNegativeDeliveryPromiseAction
        },
        [CallToActionType.ACCEPT_POSITIVE_DELIVERY_PROMISE]: {
            isSilentAction: true,
            action: acceptPositiveDeliveryPromiseAction
        },
        [CallToActionType.CHANGE_QUANTITIES]: {
            visual: changeQuantityCTAVisual,
            action: changeQuantitiesAction
        },
        [CallToActionType.CHANGE_STORE]: {
            /* 
                CHANGE_STORE kan terugkomen zonder products, 
                in dit geval moet ineens de action uitgevoerd worden. 
            **/
            isSilentAction: !hasProduct,
            visual: hasProduct ? changeStoreVisual : undefined,
            action: changeStoreAction,
            isExtended: true
        },
        [CallToActionType.CHOOSE_STORE]: {
            /* 
                CHOOSE_STORE kan terugkomen zonder products, 
                in dit geval moet ineens de action uitgevoerd worden. 
            **/
            isSilentAction: !hasProduct,
            visual: hasProduct ? changeStoreVisual : undefined,
            action: changeStoreAction,
            isExtended: true
        },
        [CallToActionType.CHANGE_STORE_AND_CNC]: {
            visual: baseCTAVisual,
            action: changeStoreAndCnCAction,
            isExtended: true
        },
        [CallToActionType.CHANGE_STORE_TO_ECORNER]: {
            isSilentAction: true,
            visual: undefined,
            action: changeECornerStoreAction,
            isExtended: true
        }
    };

    const result = managedCtas[ctaType];

    if (!result) {
        throw Error(`CTA can not be handled - ${ctaType}`);
    }

    return result;
};

export const getKeyForCTA = (ctas: Array<CTAActions>): string => {
    if (
        ctas.some((cta) =>
            [
                CallToActionType.ACCEPT_NEGATIVE_DELIVERY_PROMISE,
                CallToActionType.CHANGE_QUANTITIES
            ].some((c) => c === cta.type)
        )
    ) {
        return "notEnoughStock";
    }

    return underScoreToCamelCase(head(ctas)!.type);
};
